import {
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
  useNavigate,
  useRouterState,
  useParams,
  redirect,
} from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from '@/lib/auth';
import { SupportProvider } from '@/lib/support-context';
import { SupportWidget } from '@/components/support-widget';
import { AppShell } from '@/components/layout/app-shell';
import { LandingPage } from '@/pages/landing';
import { SignupPage, LoginPage, VerifyEmailPage, ForgotPasswordPage, ResetPasswordPage } from '@/pages/auth';
import { OnboardingPage } from '@/pages/onboarding';
import { DashboardPage } from '@/pages/dashboard';
import { RetentionPage } from '@/pages/retention';
import { CampaignsPage } from '@/pages/campaigns';
import { OpportunitiesPage } from '@/pages/opportunities';
import { UpgradesPage } from '@/pages/upgrades';
import { InsightsPage } from '@/pages/insights';
import { AgreementsPage, NewAgreementPage } from '@/pages/agreements';
import { PayPage } from '@/pages/pay';
import { UnsubscribePage } from '@/pages/unsubscribe';
import { CustomersPage, NewCustomerPage, CustomerDetailPage } from '@/pages/customers';
import { RemindersPage } from '@/pages/reminders';
import {
  SettingsPage,
  PaymentsPage,
  TemplatesPage,
  HelpPage,
  NotFoundPage,
} from '@/pages/settings';
import { BillingPage } from '@/pages/billing';
import {
  TermsPage,
  PrivacyPage,
  RefundsPage,
  CookiesPage,
  AcceptableUsePage,
  DisclaimerPage,
  SubprocessorsPage,
  SecurityPage,
  SmsTermsPage,
  DpaPage,
  AccessibilityPage,
  DataRequestsPage,
} from '@/pages/legal';
import { IntegrationsPage } from '@/pages/settings/integrations';
import { BlogIndexPage } from '@/pages/marketing/blog-index';
import { BlogPostPage } from '@/pages/marketing/blog-post';
import { NicheLandingPage } from '@/pages/marketing/niche-landing';
import { getNicheConfig, NICHE_SLUGS } from '@maintenance-tracker/shared';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

function ProtectedLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate({ to: '/login', replace: true });
      return;
    }
    if (!user.settings?.onboardingComplete && !pathname.startsWith('/onboarding')) {
      navigate({ to: '/onboarding', replace: true });
    }
  }, [user, loading, pathname, navigate]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-text-secondary">Loading…</div>;
  }
  if (!user) return null;
  if (!user.settings?.onboardingComplete && !pathname.startsWith('/onboarding')) return null;

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const rootRoute = createRootRoute({
  component: () => (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SupportProvider>
          <Outlet />
          <SupportWidget />
        </SupportProvider>
      </AuthProvider>
    </QueryClientProvider>
  ),
});

const publicRoutes = [
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: LandingPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/signup', component: SignupPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/verify-email', component: VerifyEmailPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/forgot-password', component: ForgotPasswordPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/reset-password', component: ResetPasswordPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/privacy', component: PrivacyPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/terms', component: TermsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/refunds', component: RefundsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/cookies', component: CookiesPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/acceptable-use', component: AcceptableUsePage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/disclaimer', component: DisclaimerPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/subprocessors', component: SubprocessorsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/security', component: SecurityPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/sms-terms', component: SmsTermsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/dpa', component: DpaPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/accessibility', component: AccessibilityPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/data-requests', component: DataRequestsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/blog', component: BlogIndexPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/blog/$slug',
    component: function BlogPostRoute() {
      const { slug } = useParams({ strict: false }) as { slug: string };
      return <BlogPostPage slug={slug} />;
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/for/$niche',
    beforeLoad: ({ params }) => {
      if (!NICHE_SLUGS.includes(params.niche as (typeof NICHE_SLUGS)[number])) {
        throw redirect({ to: '/' });
      }
    },
    component: function NicheRoute() {
      const { niche } = useParams({ strict: false }) as { niche: string };
      const config = getNicheConfig(niche);
      if (!config) return null;
      return <NicheLandingPage config={config} />;
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/pay/$token',
    component: function PayRoute() {
      return <PayPage />;
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/unsubscribe',
    component: UnsubscribePage,
  }),
];

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: ProtectedLayout,
});

const appRoutes = [
  createRoute({ getParentRoute: () => protectedRoute, path: '/onboarding', component: OnboardingPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/dashboard', component: DashboardPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/retention', component: RetentionPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/campaigns', component: CampaignsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/opportunities', component: OpportunitiesPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/upgrades', component: UpgradesPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/insights', component: InsightsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/agreements', component: AgreementsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/agreements/new', component: NewAgreementPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/customers', component: CustomersPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/customers/new', component: NewCustomerPage }),
  createRoute({
    getParentRoute: () => protectedRoute,
    path: '/customers/$id',
    component: function CustomerDetailRoute() {
      const { id } = useParams({ strict: false }) as { id: string };
      return <CustomerDetailPage id={id} />;
    },
  }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/reminders', component: RemindersPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/settings', component: SettingsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/settings/billing', component: BillingPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/settings/payments', component: PaymentsPage }),
  createRoute({
    getParentRoute: () => protectedRoute,
    path: '/integrations',
    beforeLoad: () => {
      throw redirect({ to: '/settings/integrations' });
    },
  }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/settings/integrations', component: IntegrationsPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/settings/templates', component: TemplatesPage }),
  createRoute({ getParentRoute: () => protectedRoute, path: '/help', component: HelpPage }),
];

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: NotFoundPage,
});

const routeTree = rootRoute.addChildren([
  ...publicRoutes,
  protectedRoute.addChildren(appRoutes),
  notFoundRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
